import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('dockhta_token');
      const storedUser = localStorage.getItem('dockhta_user');
      
      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        
        // Verify token is still valid
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('dockhta_user', JSON.stringify(response.data));
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('dockhta_token');
          localStorage.removeItem('dockhta_user');
          setToken(null);
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token: newToken, user: newUser } = response.data;
    
    localStorage.setItem('dockhta_token', newToken);
    localStorage.setItem('dockhta_user', JSON.stringify(newUser));
    
    setToken(newToken);
    setUser(newUser);
    
    return newUser;
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    const { user: newUser } = response.data;
    
    // NE PAS connecter automatiquement - l'utilisateur doit vérifier son email d'abord
    // On ne sauvegarde pas le token pour forcer la connexion après vérification
    
    return { user: newUser, otpSent: response.data.otp_sent };
  };

  const verifyOTP = async (email, otp) => {
    const response = await authAPI.verifyOTP({ email, otp });
    // NE PAS connecter automatiquement - l'utilisateur doit se connecter manuellement
    // Cela garantit un flux sécurisé : inscription -> vérification -> connexion
    return response.data;
  };

  const resendOTP = async (email) => {
    const response = await authAPI.resendOTP({ email });
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await authAPI.forgotPassword({ email });
    return response.data;
  };

  const resetPassword = async (email, otp, newPassword) => {
    const response = await authAPI.resetPassword({ email, otp, new_password: newPassword });
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await authAPI.googleSession(sessionId);
    const { token: newToken, user: newUser } = response.data;
    
    localStorage.setItem('dockhta_token', newToken);
    localStorage.setItem('dockhta_user', JSON.stringify(newUser));
    
    setToken(newToken);
    setUser(newUser);
    
    return newUser;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('dockhta_token');
    localStorage.removeItem('dockhta_user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('dockhta_user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    register,
    verifyOTP,
    resendOTP,
    forgotPassword,
    resetPassword,
    loginWithGoogle,
    processGoogleSession,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
